import * as React from 'react'
import { ChangeEvent } from 'react'

import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'

import { addToPestPacOff } from '@user/components/Settings/EntityFormSettings/constants'
import { SettingCardOptions } from '@user/components/Settings/EntityFormSettings/FieldSettingsCard/SettingCardOptions'
import type { IEntityFormSetting } from '@user/store/entityFormSettings/typings'

import { getStyles } from './styling'
import type { FieldOption } from './typings'

interface PestPacOptionFieldProps {
  settingItem: IEntityFormSetting
  systemNonOptionalRequired: boolean
  titleValue?: string
  addToPestPacSettingsHeader: string
  addToPestPacFooter: string
  settingOptions: FieldOption[]
  onChangeLocationEnabled: (
    event: ChangeEvent<HTMLInputElement>,
    value: boolean,
  ) => void
  onChangeLocationOption: (
    event: ChangeEvent<HTMLInputElement>,
    value: string | number,
  ) => void
  isTidalThemeActive: boolean
}

/**
 * The "Add to PestPac upon saving" field's own settings UI.
 *
 * Selected by the registry's `pestPacOption` renderer. Presentational: every
 * theme-dependent value comes from `getStyles`, and all state changes are
 * raised to the parent card.
 */
export function PestPacOptionField(props: PestPacOptionFieldProps) {
  const {
    settingItem,
    systemNonOptionalRequired,
    titleValue,
    addToPestPacSettingsHeader,
    addToPestPacFooter,
    settingOptions,
    onChangeLocationEnabled,
    onChangeLocationOption,
    isTidalThemeActive,
  } = props
  const { active } = settingItem
  const theme = useTheme()
  const styles = getStyles(theme, isTidalThemeActive)

  return (
    <Box sx={styles.pestPacContainer}>
      <Typography
        variant={styles.props.pestPacHeaderVariant}
        sx={styles.pestPacHeader}
      >
        {addToPestPacSettingsHeader}
      </Typography>
      <Stack direction="row" alignItems="center" sx={styles.pestPacRow}>
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={styles.pestPacCheckboxWrapper}
        >
          <Checkbox
            onClick={(evt) => evt.stopPropagation()}
            onChange={onChangeLocationEnabled}
            checked={active}
            color="primary"
            edge={styles.props.pestPacCheckboxEdge}
            size="medium"
            disabled={systemNonOptionalRequired}
            sx={styles.pestPacCheckbox}
          />
        </Stack>
        <SettingCardOptions
          value={String(settingItem.options || addToPestPacOff)}
          title={titleValue}
          onChangeOption={onChangeLocationOption}
          footer={addToPestPacFooter}
          marginLeft={0}
          options={settingOptions}
          isTidalThemeActive={isTidalThemeActive}
        />
      </Stack>
    </Box>
  )
}
